import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminRequest, getAdminHeaders, toAssetUrl } from "./adminApi";
import AdminResetPasswordPanel from "./AdminResetPasswordPanel";
import { MARITAL_STATUS_OPTIONS } from "../../constants/maritalStatus";
import chinaRegions from "../chinaRegions.json";
import { formatLocationDisplay } from "../../utils/resolveRegion";

const emptyProfileForm = {
  name: "",
  gender: "",
  age: "",
  birth_year: "",
  birth_month: "",
  birth_day: "",
  province: "",
  city: "",
  district: "",
  marital_status: "",
  education: "",
  job: "",
  height: "",
  weight: "",
  monthly_income: "",
  housing_status: "",
  religion: "",
  bio: "",
  hobbies: "",
  partner_requirement: "",
  unlock_birth: false,
  profile_completed: 1,
};

function userToForm(user) {
  return {
    name: user.name || "",
    gender: user.gender || "",
    age: user.age != null ? String(user.age) : "",
    birth_year: user.birth_year != null ? String(user.birth_year) : "",
    birth_month: user.birth_month != null ? String(user.birth_month) : "",
    birth_day: user.birth_day != null ? String(user.birth_day) : "",
    province: user.province || "",
    city: user.city || "",
    district: user.district || "",
    marital_status: user.marital_status || "",
    education: user.education || "",
    job: user.job || "",
    height: user.height != null ? String(user.height) : "",
    weight: user.weight != null ? String(user.weight) : "",
    monthly_income: user.monthly_income || "",
    housing_status: user.housing_status || "",
    religion: user.religion || "",
    bio: user.bio || "",
    hobbies: user.hobbies || "",
    partner_requirement: user.partner_requirement || "",
    unlock_birth: false,
    profile_completed: user.profile_completed ? 1 : 0,
  };
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyProfileForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const cities = useMemo(() => {
    const province = chinaRegions.find((item) => item.name === form.province);
    return province?.children || [];
  }, [form.province]);

  const districts = useMemo(() => {
    const city = cities.find((item) => item.name === form.city);
    return city?.children || [];
  }, [cities, form.city]);

  const birthYears = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: now - 1940 + 1 }, (_, i) => now - i);
  }, []);

  const fetchUser = async () => {
    try {
      const { res, data } = await adminRequest(`/api/admin/users/${userId}`, {
        headers: getAdminHeaders(),
      });

      if (!res.ok) {
        alert(data?.detail || "获取用户资料失败");
        navigate("/admin/users");
        return;
      }

      setUser(data);
      setForm(userToForm(data));
    } catch (err) {
      console.error(err);
      alert("获取用户资料失败，请确认后端已启动");
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!String(form.name || "").trim()) {
      alert("请填写姓名");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        gender: form.gender || "",
        province: form.province || "",
        city: form.city || "",
        district: form.district || "",
        marital_status: form.marital_status || "",
        education: form.education || "",
        job: form.job || "",
        monthly_income: form.monthly_income || "",
        housing_status: form.housing_status || "",
        religion: form.religion || "未填写",
        bio: form.bio || "",
        hobbies: form.hobbies || "",
        partner_requirement: form.partner_requirement || "",
        profile_completed: Number(form.profile_completed) ? 1 : 0,
        unlock_birth: form.unlock_birth ? 1 : 0,
        height: form.height ? Number(form.height) : null,
        weight: form.weight ? Number(form.weight) : null,
      };
      if (form.birth_year && form.birth_month && form.birth_day) {
        payload.birth_year = Number(form.birth_year);
        payload.birth_month = Number(form.birth_month);
        payload.birth_day = Number(form.birth_day);
      } else if (form.age) {
        payload.age = Number(form.age);
      }

      const { res, data } = await adminRequest(
        `/api/admin/users/${userId}/profile`,
        {
          method: "PATCH",
          headers: getAdminHeaders(true),
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        alert(data?.detail || "保存失败");
        return;
      }
      alert("资料已保存");
      setEditing(false);
      await fetchUser();
    } catch (err) {
      console.error(err);
      alert("保存失败");
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailable = async () => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${userId}/toggle-available`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchUser();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const auditUser = async (approved) => {
    const rejectReason = approved ? "" : window.prompt("请输入不通过原因", "");

    if (!approved && rejectReason === null) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${userId}/audit`,
        {
          method: "POST",
          headers: getAdminHeaders(true),
          body: JSON.stringify({
            approved,
            reason: rejectReason || "",
          }),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "审核失败");
        return;
      }

      alert(approved ? "已审核通过" : "已取消审核");
      fetchUser();
    } catch (err) {
      console.error(err);
      alert("审核失败");
    }
  };

  const deleteUser = async () => {
    const name = user.name || user.phone || `ID ${user.id}`;
    if (
      !window.confirm(
        `确定删除用户「${name}」吗？\n此操作不可恢复，关联的消息和资料也会一并删除。`
      )
    ) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${userId}/delete`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || data?.msg || "删除失败");
        return;
      }

      alert("用户已删除");
      navigate("/admin/users");
    } catch (err) {
      console.error(err);
      alert("删除失败");
    }
  };

  if (loading) {
    return <div className="text-gray-400">加载中...</div>;
  }

  if (!user) {
    return <div className="text-gray-400">用户不存在</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate("/admin/users")}
        className="mb-6 text-gray-500 hover:text-blue-600"
      >
        返回用户列表
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <section className="flex-1 space-y-6">
          <div className="bg-white rounded-2xl shadow p-6">
            <div className="flex items-center gap-4">
              <img
                src={toAssetUrl(user.avatar) || "/default-avatar.png"}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover bg-gray-100"
              />

              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {user.name || "未命名用户"}
                </h1>
                <p className="text-gray-400 mt-1">{user.phone || "-"}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Badge ok={user.profile_completed} text="资料" />
                  <Badge ok={user.has_questionnaire} text="测评" />
                  <Badge
                    ok={
                      user.verification?.real_name_verified || user.is_id_verified
                    }
                    text="实名"
                  />
                  <Badge ok={user.verification?.face_verified} text="真人" />
                  <Badge ok={user.verification?.platform_verified} text="平台" />
                  <Badge ok={user.is_available} text="前台展示" />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  认证等级：
                  {user.verification?.verification_level_label || "未认证"}
                  （L{user.verification?.verification_level ?? 0}）
                </p>
              </div>
            </div>
          </div>

          <InfoBlock title="分层认证">
            <VerificationAdminPanel user={user} onChanged={fetchUser} />
          </InfoBlock>

          <InfoBlock
            title="基础资料"
            action={
              editing ? null : (
                <button
                  type="button"
                  onClick={() => {
                    setForm(userToForm(user));
                    setEditing(true);
                  }}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                >
                  修改资料
                </button>
              )
            }
          >
            {editing ? (
              <AdminProfileForm
                form={form}
                setForm={setForm}
                cities={cities}
                districts={districts}
                birthYears={birthYears}
                ageLocked={Boolean(user.age_locked)}
                saving={saving}
                onSave={saveProfile}
                onCancel={() => {
                  setForm(userToForm(user));
                  setEditing(false);
                }}
              />
            ) : (
              <InfoGrid
                items={[
                  ["性别", user.gender],
                  ["年龄", user.age],
                  [
                    "出生日期",
                    user.birth_year && user.birth_month && user.birth_day
                      ? `${user.birth_year}-${user.birth_month}-${user.birth_day}${
                          user.age_locked ? "（已锁定）" : ""
                        }`
                      : "",
                  ],
                  [
                    "地区",
                    formatLocationDisplay(
                      user.province,
                      user.city,
                      user.district,
                      { separator: " ", fallback: "" }
                    ),
                  ],
                  ["学历", user.education],
                  ["职业", user.job],
                  ["收入", user.monthly_income],
                  ["婚姻状态", user.marital_status],
                  ["住房", user.housing_status],
                  ["宗教", user.religion],
                  ["身高", user.height ? `${user.height}cm` : ""],
                  ["体重", user.weight ? `${user.weight}kg` : ""],
                ]}
              />
            )}
          </InfoBlock>

          <InfoBlock title="个人介绍">
            {editing ? (
              <p className="text-sm text-gray-400">请在上方「修改资料」表单中编辑自我介绍、兴趣爱好与择偶要求。</p>
            ) : (
              <>
                <TextLine label="自我介绍" value={user.bio} />
                <TextLine label="兴趣爱好" value={user.hobbies} />
                <TextLine label="择偶要求" value={user.partner_requirement} />
              </>
            )}
          </InfoBlock>

          <InfoBlock title="生活照">
            {Array.isArray(user.photos) && user.photos.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-4">
                {user.photos.map((photo) => (
                  <img
                    key={photo.id || photo.image_url}
                    src={toAssetUrl(photo.image_url || photo)}
                    alt="生活照"
                    className="w-full aspect-[4/3] rounded-xl object-cover bg-gray-100"
                  />
                ))}
              </div>
            ) : (
              <p className="text-gray-400">未上传生活照</p>
            )}
          </InfoBlock>

          <InfoBlock title="身份证材料">
            <div className="grid md:grid-cols-2 gap-4">
              <IdImage title="身份证正面" url={user.id_front_url} />
              <IdImage title="身份证反面" url={user.id_back_url} />
            </div>
          </InfoBlock>
        </section>

        <aside className="w-full lg:w-80">
          <div className="bg-white rounded-2xl shadow p-6 sticky top-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">审核操作</h2>
            <div className="space-y-3 text-sm text-gray-600 mb-6">
              <InfoRow
                label="认证等级"
                value={
                  user.verification?.verification_level_label ||
                  (user.is_id_verified ? "实名认证" : "未认证")
                }
              />
              <InfoRow
                label="资料认证"
                value={
                  user.verification?.profile_status === "pending"
                    ? "待审核"
                    : user.verification?.profile_verified
                    ? "已通过"
                    : user.verification?.profile_status === "rejected"
                    ? "已拒绝"
                    : "未申请"
                }
              />
              <InfoRow
                label="身份证实名"
                value={user.is_id_verified ? "已通过" : "待审核/未提交"}
              />
              <InfoRow
                label="前台展示"
                value={user.is_available ? "展示中" : "已隐藏"}
              />
              <InfoRow
                label="账号状态"
                value={user.is_disabled ? "已禁用" : "正常"}
              />
              <InfoRow label="注册时间" value={formatTime(user.created_at)} />
            </div>

            <p className="text-xs text-gray-400 mb-2">身份证人工审核（旧流程）</p>
            <button
              onClick={() => auditUser(true)}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
            >
              身份证实名通过
            </button>
            <button
              onClick={() => auditUser(false)}
              className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"
            >
              取消实名 / 不通过
            </button>
            <button
              onClick={toggleAvailable}
              className="w-full mt-3 py-3 rounded-xl border border-blue-200 text-blue-600 font-bold hover:bg-blue-50"
            >
              {user.is_available ? "隐藏前台展示" : "开启前台展示"}
            </button>
            <button
              onClick={() =>
                navigate("/admin/messages", {
                  state: {
                    recipientType: "user",
                    recipientId: user.id,
                  },
                })
              }
              className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"
            >
              发送系统消息
            </button>
            <button
              onClick={deleteUser}
              className="w-full mt-3 py-3 rounded-xl border border-red-200 text-red-500 font-bold hover:bg-red-50"
            >
              删除用户
            </button>

            <AdminResetPasswordPanel
              role="user"
              accountId={user.id}
              phone={user.phone}
              name={user.name}
              email={user.email}
              emailVerified={Boolean(user.email_verified)}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}

function VerificationAdminPanel({ user, onChanged }) {
  const v = user.verification || {};
  const records = Array.isArray(user.verification_records)
    ? user.verification_records
    : [];
  const [busy, setBusy] = useState(false);

  const auditProfile = async (approved) => {
    const reason = approved
      ? ""
      : window.prompt("请输入资料认证不通过原因", "") ?? null;
    if (!approved && reason === null) return;

    try {
      setBusy(true);
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/verification/profile`,
        {
          method: "POST",
          headers: getAdminHeaders(true),
          body: JSON.stringify({ approved, reason: reason || "" }),
        }
      );
      if (!res.ok) {
        alert(data?.detail || "资料认证审核失败");
        return;
      }
      alert(approved ? "资料认证已通过" : "资料认证已拒绝");
      onChanged();
    } catch (err) {
      console.error(err);
      alert("资料认证审核失败");
    } finally {
      setBusy(false);
    }
  };

  const togglePlatform = async (enabled) => {
    const tip = enabled
      ? "确认授予平台认证？（需已完成实名、真人、资料认证）"
      : "确认取消该用户的平台认证？";
    if (!window.confirm(tip)) return;

    try {
      setBusy(true);
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/verification/platform`,
        {
          method: "POST",
          headers: getAdminHeaders(true),
          body: JSON.stringify({ enabled }),
        }
      );
      if (!res.ok) {
        alert(data?.detail || "平台认证操作失败");
        return;
      }
      alert(enabled ? "已授予平台认证" : "已取消平台认证");
      onChanged();
    } catch (err) {
      console.error(err);
      alert("平台认证操作失败");
    } finally {
      setBusy(false);
    }
  };

  const layers = [
    {
      key: "phone",
      label: "手机认证",
      ok: v.phone_verified,
      meta: v.phone_verified_at
        ? `通过于 ${formatTime(v.phone_verified_at)}`
        : "未完成",
    },
    {
      key: "real_name",
      label: "实名认证",
      ok: v.real_name_verified || user.is_id_verified,
      meta: [
        v.real_name_masked ? `姓名 ${v.real_name_masked}` : null,
        v.real_name_provider ? `来源 ${v.real_name_provider}` : null,
        v.real_name_verified_at
          ? formatTime(v.real_name_verified_at)
          : user.is_id_verified
          ? "身份证人工审核"
          : "未完成",
      ]
        .filter(Boolean)
        .join(" · "),
    },
    {
      key: "face",
      label: "真人认证",
      ok: v.face_verified,
      meta: v.face_verified_at
        ? `${v.face_verified_provider || ""} ${formatTime(v.face_verified_at)}`.trim()
        : "未完成",
    },
    {
      key: "profile",
      label: "资料认证",
      ok: v.profile_verified,
      meta:
        v.profile_status === "pending"
          ? "待人工审核"
          : v.profile_status === "rejected"
          ? `已拒绝：${v.profile_reject_reason || "无原因"}`
          : v.profile_verified_at
          ? `通过于 ${formatTime(v.profile_verified_at)}`
          : "未申请",
    },
    {
      key: "platform",
      label: "平台认证",
      ok: v.platform_verified,
      meta: v.platform_verified_at
        ? `授予于 ${formatTime(v.platform_verified_at)}`
        : "未授予",
    },
  ];

  const typeLabel = {
    phone: "手机",
    real_name: "实名",
    face: "真人",
    profile: "资料",
    platform: "平台",
    id_card: "身份证",
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {layers.map((item) => (
          <div
            key={item.key}
            className={`rounded-xl border p-4 ${
              item.ok
                ? "border-green-200 bg-green-50"
                : item.key === "profile" && v.profile_status === "pending"
                ? "border-amber-200 bg-amber-50"
                : "border-gray-100 bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold text-gray-800">{item.label}</p>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  item.ok
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {item.ok ? "已通过" : "未通过"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2 leading-5">{item.meta}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {(v.profile_status === "pending" || !v.profile_verified) && (
          <button
            type="button"
            disabled={busy}
            onClick={() => auditProfile(true)}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            资料认证通过
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => auditProfile(false)}
          className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          资料认证拒绝
        </button>
        {v.platform_verified ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => togglePlatform(false)}
            className="px-4 py-2 rounded-xl border border-amber-200 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          >
            取消平台认证
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => togglePlatform(true)}
            className="px-4 py-2 rounded-xl border border-blue-200 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            授予平台认证
          </button>
        )}
      </div>

      <div>
        <p className="font-bold text-gray-700 mb-2">认证流水（近 30 条）</p>
        {records.length === 0 ? (
          <p className="text-sm text-gray-400">暂无流水</p>
        ) : (
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-3">类型</th>
                  <th className="text-left p-3">状态</th>
                  <th className="text-left p-3">渠道</th>
                  <th className="text-left p-3">说明</th>
                  <th className="text-left p-3">时间</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {records.map((row) => (
                  <tr key={row.id}>
                    <td className="p-3">
                      {typeLabel[row.verification_type] || row.verification_type}
                    </td>
                    <td className="p-3">{row.status}</td>
                    <td className="p-3 text-gray-500">{row.provider || "-"}</td>
                    <td className="p-3 text-gray-500">
                      {row.failure_reason || "-"}
                    </td>
                    <td className="p-3 text-gray-500">
                      {formatTime(row.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoBlock({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function AdminProfileForm({
  form,
  setForm,
  cities,
  districts,
  birthYears,
  ageLocked,
  saving,
  onSave,
  onCancel,
}) {
  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const inputClass = "w-full border rounded-lg px-3 py-2 text-sm";

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-sm space-y-1">
          <span className="text-gray-500">姓名 *</span>
          <input className={inputClass} value={form.name} onChange={field("name")} />
        </label>
        <label className="text-sm space-y-1">
          <span className="text-gray-500">性别</span>
          <select className={inputClass} value={form.gender} onChange={field("gender")}>
            <option value="">请选择</option>
            <option value="男">男</option>
            <option value="女">女</option>
          </select>
        </label>
        <label className="text-sm space-y-1">
          <span className="text-gray-500">年龄（无生日时可用）</span>
          <input
            type="number"
            className={inputClass}
            value={form.age}
            onChange={field("age")}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="text-gray-500">婚姻状态</span>
          <select
            className={inputClass}
            value={form.marital_status}
            onChange={field("marital_status")}
          >
            <option value="">请选择</option>
            {MARITAL_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 space-y-2">
        <p className="text-sm text-amber-800 font-medium">
          出生日期
          {ageLocked ? "（会员端已锁定，管理员可改）" : ""}
        </p>
        <div className="grid grid-cols-3 gap-2">
          <select className={inputClass} value={form.birth_year} onChange={field("birth_year")}>
            <option value="">年</option>
            {birthYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select className={inputClass} value={form.birth_month} onChange={field("birth_month")}>
            <option value="">月</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
          <select className={inputClass} value={form.birth_day} onChange={field("birth_day")}>
            <option value="">日</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-amber-900">
          <input
            type="checkbox"
            checked={Boolean(form.unlock_birth)}
            onChange={(e) => setForm({ ...form, unlock_birth: e.target.checked })}
          />
          同时解除出生日期锁定（允许会员自行再改）
        </label>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <select
          className={inputClass}
          value={form.province}
          onChange={(e) =>
            setForm({ ...form, province: e.target.value, city: "", district: "" })
          }
        >
          <option value="">省</option>
          {chinaRegions.map((province) => (
            <option key={province.code} value={province.name}>
              {province.name}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value, district: "" })}
        >
          <option value="">市</option>
          {cities.map((city) => (
            <option key={city.code} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>
        <select className={inputClass} value={form.district} onChange={field("district")}>
          <option value="">区/县</option>
          {districts.map((district) => (
            <option key={district.code} value={district.name}>
              {district.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <select className={inputClass} value={form.education} onChange={field("education")}>
          <option value="">学历</option>
          {["高中", "大专", "本科", "硕士", "博士"].map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <input
          className={inputClass}
          placeholder="职业"
          value={form.job}
          onChange={field("job")}
        />
        <select
          className={inputClass}
          value={form.monthly_income}
          onChange={field("monthly_income")}
        >
          <option value="">月收入</option>
          {["5k以下", "5k-10k", "10k-20k", "20k-50k", "50k以上"].map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={form.housing_status}
          onChange={field("housing_status")}
        >
          <option value="">住房情况</option>
          {["已购房", "租房", "与家人同住", "暂无住房"].map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <input
          type="number"
          className={inputClass}
          placeholder="身高 cm"
          value={form.height}
          onChange={field("height")}
        />
        <input
          type="number"
          className={inputClass}
          placeholder="体重 kg"
          value={form.weight}
          onChange={field("weight")}
        />
        <select className={inputClass} value={form.religion} onChange={field("religion")}>
          <option value="">宗教信仰</option>
          {["未填写", "无", "基督教", "天主教", "东正教", "佛教", "伊斯兰教", "道教", "其他"].map(
            (item) => (
              <option key={item} value={item}>
                {item}
              </option>
            )
          )}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={Number(form.profile_completed) === 1}
            onChange={(e) =>
              setForm({ ...form, profile_completed: e.target.checked ? 1 : 0 })
            }
          />
          标记资料已完善
        </label>
      </div>

      <textarea
        className={inputClass}
        rows={3}
        placeholder="自我介绍"
        value={form.bio}
        onChange={field("bio")}
      />
      <textarea
        className={inputClass}
        rows={2}
        placeholder="兴趣爱好"
        value={form.hobbies}
        onChange={field("hobbies")}
      />
      <textarea
        className={inputClass}
        rows={2}
        placeholder="择偶要求"
        value={form.partner_requirement}
        onChange={field("partner_requirement")}
      />

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "保存中..." : "保存资料"}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          className="px-5 py-2.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50"
        >
          取消
        </button>
      </div>
    </div>
  );
}

function InfoGrid({ items }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map(([label, value]) => (
        <InfoRow key={label} label={label} value={value} />
      ))}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-gray-700 font-medium mt-1">{value || "未填写"}</p>
    </div>
  );
}

function TextLine({ label, value }) {
  return (
    <div className="mb-4 last:mb-0">
      <p className="text-gray-400 text-sm mb-1">{label}</p>
      <p className="text-gray-700 leading-7 whitespace-pre-wrap">
        {value || "未填写"}
      </p>
    </div>
  );
}

function IdImage({ title, url }) {
  const src = toAssetUrl(url);

  return (
    <div>
      <p className="font-bold text-gray-700 mb-3">{title}</p>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img
            src={src}
            alt={title}
            className="w-full aspect-[16/10] rounded-xl object-cover bg-gray-100 border"
          />
        </a>
      ) : (
        <div className="aspect-[16/10] rounded-xl bg-gray-50 border flex items-center justify-center text-gray-400">
          未上传
        </div>
      )}
    </div>
  );
}

function Badge({ ok, text }) {
  return (
    <span
      className={`text-xs px-2 py-1 rounded-full ${
        ok ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
      }`}
    >
      {ok ? `${text}已完成` : `${text}未完成`}
    </span>
  );
}

function formatTime(time) {
  return time ? new Date(time).toLocaleString() : "未记录";
}
